import { API_CONFIG } from '../config/api';

interface User {
  id: number;
  name: string;
  created_date: string;
  has_biometry: boolean;
}

interface UserListResponse {
  content: User[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface UserAttribute {
  key_name: string;
  key_value: string;
  created_date: string;
}

interface UserDetails extends User {
  attributes: UserAttribute[];
}

interface CreateUserResponse {
  id: number;
  name: string;
  created_date: string;
}

interface BiometryImageResponse {
  image: string; // base64 string
}

interface CreateBiometryRequest {
  user_id: number;
  img_b64: string;
}

interface DeleteAttributeRequest {
  key_name: string;
  key_value: string;
}

interface CreateAttributeRequest {
  key_name: string;
  key_value: string;
}

interface UpdateUserRequest {
  name: string;
}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

export const userService = {
  async listUsers(page: number = 1, perPage: number = 10): Promise<UserListResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}?page=${page}&per_page=${perPage}`,
        {
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async getUserDetails(userId: number): Promise<UserDetails> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}`,
        {
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async createUser(name: string): Promise<CreateUserResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ name }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create user');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async deleteUser(id: number): Promise<void> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${id}`,
        {
          method: 'DELETE',
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      throw error;
    }
  },

  async getUserBiometryImage(userId: number): Promise<string | null> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}/image`,
        {
          headers: getHeaders(),
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch user biometry image');
      }

      const data: BiometryImageResponse = await response.json();
      return data.image;
    } catch (error) {
      throw error;
    }
  },

  async createBiometry(data: CreateBiometryRequest): Promise<void> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${data.user_id}`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            img_b64: data.img_b64,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create biometry');
      }
    } catch (error) {
      throw error;
    }
  },

  async updateBiometry(userId: number, img_b64: string): Promise<void> {
    try {
      const formattedImage = img_b64.startsWith('data:image')
        ? img_b64
        : `data:image/jpeg;base64,${img_b64}`;

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}`,
        {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({ 
            name: null,
            img_b64: formattedImage
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update biometry: ${JSON.stringify(errorData)}`);
      }
    } catch (error) {
      throw error;
    }
  },

  async deleteBiometry(userId: number): Promise<void> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}/biometry`,
        {
          method: 'DELETE',
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete biometry');
      }
    } catch (error) {
      throw error;
    }
  },

  async deleteAttribute(userId: number, data: DeleteAttributeRequest): Promise<void> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}/attributes`,
        {
          method: 'DELETE',
          headers: getHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete attribute');
      }
    } catch (error) {
      throw error;
    }
  },

  async createAttribute(userId: number, data: CreateAttributeRequest): Promise<void> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}/attributes`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create attribute');
      }
    } catch (error) {
      throw error;
    }
  },

  async updateUser(userId: number, data: UpdateUserRequest): Promise<void> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/${userId}`,
        {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      throw error;
    }
  },
}; 